import { useMemo, useState } from "react"

interface SpendingChartProps {
  data: { date: string; amount: number }[]
  budget?: number | null
  budgetLabel?: string
  currencySymbol?: string
  year?: number
  month?: number | null
  onDateClick?: (date: string) => void
}

function fillDailyGaps(data: { date: string; amount: number }[], year: number, month: number) {
  const byDate: Record<string, number> = {}
  data.forEach(d => { byDate[d.date] = d.amount })
  const today = new Date()
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month
  const daysInMonth = new Date(year, month, 0).getDate()
  const lastDay = isCurrentMonth ? today.getDate() : daysInMonth
  return Array.from({ length: lastDay }, (_, i) => {
    const day = i + 1
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return { date: dateStr, amount: byDate[dateStr] ?? 0 }
  })
}

export function SpendingChart({
  data,
  budget,
  currencySymbol = '$',
  year,
  month,
  onDateClick,
}: SpendingChartProps) {
  const isMonthly = month != null && year != null

  const points = useMemo(() => {
    if (isMonthly) return fillDailyGaps(data, year!, month!)
    return data
  }, [data, isMonthly, year, month])

  const withCumulative = useMemo(() =>
    points.reduce((acc, p) => {
      const cumulative = (acc[acc.length - 1]?.cumulative ?? 0) + Math.max(0, p.amount)
      return [...acc, { ...p, cumulative }]
    }, [] as { date: string; amount: number; cumulative: number }[])
  , [points])

  const total = withCumulative[withCumulative.length - 1]?.cumulative ?? 0

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground gap-2">
        <p className="text-sm">No spending data for this period</p>
        <p className="text-xs opacity-60">Add an expense to see your trends</p>
      </div>
    )
  }

  return (
    <div>
      <div className="relative flex items-center justify-between mb-3">
        <span className="text-base font-semibold">Spending Over Time</span>
        <div className="flex items-center gap-3 absolute left-1/2 -translate-x-1/2">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-border" />
            <span className="text-[10px] font-mono text-muted-foreground">daily</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-0.5 rounded bg-primary" />
            <span className="text-[10px] font-mono text-muted-foreground">total</span>
          </div>
        </div>
        <span className="text-base font-semibold text-primary font-mono">{currencySymbol}{total.toFixed(0)}</span>
      </div>
      <OverlayChart
        points={withCumulative}
        budget={budget}
        currencySymbol={currencySymbol}
        isMonthly={isMonthly}
        onDateClick={onDateClick}
      />
    </div>
  )
}

function OverlayChart({
  points,
  budget,
  currencySymbol = '$',
  isMonthly,
  onDateClick,
}: {
  points: { date: string; amount: number; cumulative: number }[]
  budget?: number | null
  currencySymbol?: string
  isMonthly: boolean
  onDateClick?: (date: string) => void
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const n = points.length
  if (n === 0) return null

  // SVG dimensions — viewBox so it scales to container width
  const W = 560, H = 160
  const pad = { t: 8, r: 8, b: 24, l: 44 }
  const cW = W - pad.l - pad.r
  const cH = H - pad.t - pad.b

  // Include budget in the scale so the budget line is always visible, even when under budget.
  const maxCum = Math.max(...points.map(d => d.cumulative), budget ?? 0) * 1.15 || 100
  const maxDaily = Math.max(...points.map(d => Math.abs(d.amount))) || 1
  const barW = isMonthly
    ? Math.max(2, cW / n - 1.5)
    : Math.max(8, Math.min(28, (cW / n) * 0.5))

  // Inset x range by half bar width so first/last bars stay fully inside the chart area
  const x = (i: number) => pad.l + barW / 2 + (i / Math.max(n - 1, 1)) * (cW - barW)
  const barX = (i: number) => x(i) - barW / 2
  const yCum = (v: number) => pad.t + cH - (v / maxCum) * cH

  const cumPts = points.map((d, i) => [x(i), yCum(d.cumulative)] as [number, number])
  const polyline = cumPts.map(([px, py]) => `${px},${py}`).join(' ')
  const area = cumPts.length > 1
    ? `M${cumPts[0][0]},${pad.t + cH} ` + cumPts.map(([px, py]) => `L${px},${py}`).join(' ') + ` L${cumPts[n - 1][0]},${pad.t + cH}Z`
    : ''

  const budgetY = budget ? yCum(budget) : null
  const showBudget = budgetY !== null && budgetY >= pad.t && budgetY <= pad.t + cH
  const ticks = [0, Math.round(maxCum * 0.5), Math.round(maxCum * 0.9)]

  // X labels: every 7 days for monthly, every month initial for yearly
  const labelIndices = isMonthly
    ? points.map((_, i) => i).filter(i => i % 7 === 0 || i === n - 1)
    : points.map((_, i) => i)

  const getLabel = (d: { date: string }) => {
    const parts = d.date.split('-')
    if (isMonthly) return String(parseInt(parts[2], 10))
    const m = parseInt(parts[1], 10)
    return ['J','F','M','A','M','J','J','A','S','O','N','D'][m - 1]
  }

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
      {/* Y gridlines + labels */}
      {ticks.map(v => {
        const yv = yCum(v)
        return (
          <g key={v}>
            <line x1={pad.l} y1={yv} x2={pad.l + cW} y2={yv} stroke="hsl(var(--border))" strokeWidth="1" strokeDasharray="3,3" />
            <text x={pad.l - 5} y={yv + 3.5} textAnchor="end" fontSize="9" fill="hsl(var(--muted-foreground))" fontFamily="'DM Mono', 'Courier New', monospace">
              {currencySymbol}{v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
            </text>
          </g>
        )
      })}

      {/* Budget line */}
      {showBudget && (
        <g>
          <line x1={pad.l} y1={budgetY} x2={pad.l + cW} y2={budgetY} stroke="hsl(var(--primary))" strokeWidth="1.5" strokeDasharray="5,3" opacity="0.5" />
          <text x={pad.l + cW} y={budgetY - 4} textAnchor="end" fontSize="9" fill="hsl(var(--primary))" fontFamily="'DM Mono', 'Courier New', monospace" opacity="0.7">budget</text>
        </g>
      )}

      {/* Daily bars (visual only — hit areas rendered last) */}
      {points.map((d, i) => {
        const bH = (Math.abs(d.amount) / maxDaily) * (cH * 0.5)
        const isRefund = d.amount < 0
        return bH > 0 ? (
          <rect
            key={i}
            x={barX(i)} y={pad.t + cH - bH} width={barW} height={bH}
            fill={isRefund ? 'hsl(142 71% 45%)' : 'hsl(var(--border))'}
            rx="1.5"
            pointerEvents="none"
          />
        ) : null
      })}

      {/* Cumulative area + line (no pointer events — hit areas handle interaction) */}
      {area && <path d={area} fill="hsl(var(--primary))" fillOpacity="0.08" pointerEvents="none" />}
      {cumPts.length > 1 && (
        <polyline points={polyline} fill="none" stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" pointerEvents="none" />
      )}
      {cumPts.map(([px, py], i) => points[i].amount !== 0 && (
        <circle
          key={i} cx={px} cy={py} r={hoveredIndex === i ? 4 : 2.5}
          fill="hsl(var(--primary))"
          style={{ transition: 'r 0.1s' }}
          pointerEvents="none"
        />
      ))}

      {/* Full-column hit areas — rendered last so they're topmost */}
      {points.map((d, i) => {
        const hitW = Math.max(barW, 16)
        return (
          <rect
            key={`hit-${i}`}
            x={x(i) - hitW / 2} y={pad.t} width={hitW} height={cH}
            fill="transparent"
            style={{ cursor: onDateClick ? 'pointer' : 'default' }}
            onClick={() => onDateClick?.(d.date)}
            onMouseEnter={() => setHoveredIndex(i)}
            onMouseLeave={() => setHoveredIndex(null)}
          />
        )
      })}

      {/* Hover tooltip */}
      {hoveredIndex !== null && (() => {
        const pt = points[hoveredIndex]
        const [px, py] = cumPts[hoveredIndex]
        const parts = pt.date.split('-')
        const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
        const dateLabel = isMonthly
          ? `${monthNames[parseInt(parts[1]) - 1]} ${parseInt(parts[2])}`
          : `${monthNames[parseInt(parts[1]) - 1]} ${parts[0]}`
        const tw = 88, th = pt.amount !== 0 ? 46 : 30, tr = 4
        let tx = px - tw / 2
        tx = Math.max(pad.l, Math.min(tx, pad.l + cW - tw))
        const ty = py - th - 10 < pad.t ? py + 10 : py - th - 10
        return (
          <g key="tooltip" pointerEvents="none">
            <rect x={tx} y={ty} width={tw} height={th} rx={tr}
              fill="hsl(var(--popover))" stroke="hsl(var(--border))" strokeWidth="1" />
            <text x={tx + tw / 2} y={ty + 13} textAnchor="middle" fontSize="9"
              fill="hsl(var(--muted-foreground))" fontFamily="'DM Mono', monospace">{dateLabel}</text>
            {pt.amount !== 0 && (
              <text x={tx + tw / 2} y={ty + 27} textAnchor="middle" fontSize="11" fontWeight="600"
                fill="hsl(var(--foreground))" fontFamily="'DM Mono', monospace">
                {pt.amount < 0 ? '+' : ''}{currencySymbol}{Math.abs(pt.amount).toFixed(0)}
              </text>
            )}
            <text x={tx + tw / 2} y={ty + (pt.amount !== 0 ? 40 : 24)} textAnchor="middle" fontSize="9"
              fill="hsl(var(--primary))" fontFamily="'DM Mono', monospace">
              {currencySymbol}{pt.cumulative.toFixed(0)} total
            </text>
          </g>
        )
      })()}

      {/* X labels */}
      {labelIndices.map(i => (
        <text key={i} x={x(i)} y={H - 4} textAnchor="middle" fontSize="9" fill="hsl(var(--muted-foreground))" fontFamily="'DM Mono', 'Courier New', monospace">
          {getLabel(points[i])}
        </text>
      ))}
    </svg>
  )
}
